import type { TripLeg } from "@poi-planner/shared";
import { daysBetweenDates } from "./zonedTime";

/**
 * One date's slice of a leg, shaped like `AccommodationStaySegment` so the
 * month view can treat a leg as a third consumer of the same range-across-week-
 * rows machinery. The difference is where the range comes from: an
 * accommodation stay is derived by scanning consecutive `sleep` entries, while
 * a leg carries an explicit inclusive `startDate`/`endDate`.
 *
 * `index`/`count` and `isStart`/`isEnd` describe the leg's whole range, not the
 * visible window: a leg continuing past the edge of the rendered dates still
 * reports `isStart: false` there, so the chip renders a continuation marker and
 * withholds the resize handle for an edge that is not on screen.
 */
export interface LegSegment {
  date: string;
  legId: string;
  index: number;
  count: number;
  isStart: boolean;
  isEnd: boolean;
  /** The leg continues into the previous rendered row. */
  clippedTop: boolean;
  /** The leg continues into the next rendered row. */
  clippedBottom: boolean;
}

/**
 * The segment of `leg` covering `date`, or null when the leg does not span it.
 * `rowStartDate`/`rowEndDate` bound the row the date is rendered in (a month
 * week row), which is what decides clipping.
 */
export function legSegmentForDate(
  leg: TripLeg,
  date: string,
  rowStartDate: string,
  rowEndDate: string,
): LegSegment | null {
  if (date < leg.startDate || date > leg.endDate) return null;
  const index = daysBetweenDates(leg.startDate, date);
  const count = daysBetweenDates(leg.startDate, leg.endDate) + 1;
  return {
    date,
    legId: leg.id,
    index,
    count,
    isStart: index === 0,
    isEnd: index === count - 1,
    clippedTop: leg.startDate < rowStartDate,
    clippedBottom: leg.endDate > rowEndDate,
  };
}

/**
 * Assign each leg the first lane whose previous leg ends before this one
 * starts. Non-overlapping legs therefore share a row, and only genuine
 * overlaps — a hotel-to-hotel handoff day — take a second one.
 */
export function packLegLanes(legs: TripLeg[]): Map<string, number> {
  return packDateRangeLanes(sortByStart(legs));
}

/** An inclusive date range that occupies one lane of the all-day area. */
export interface LaneRange {
  id: string;
  startDate: string;
  endDate: string;
}

function sortByStart<T extends LaneRange>(ranges: T[]): T[] {
  return [...ranges].sort(
    (a, b) =>
      a.startDate.localeCompare(b.startDate) || a.id.localeCompare(b.id),
  );
}

/**
 * Place each range, in the order given, into the first lane with no range
 * sharing any of its dates. Unlike a plain end-of-lane check this fills gaps,
 * so a later group (all-day notes after legs) can slot in beside an earlier
 * group's ranges instead of always opening a new row.
 */
export function packDateRangeLanes(
  ranges: LaneRange[],
  lanes = new Map<string, number>(),
  occupied: LaneRange[][] = [],
): Map<string, number> {
  for (const range of ranges) {
    let lane = occupied.findIndex((taken) =>
      taken.every(
        (other) =>
          other.endDate < range.startDate || other.startDate > range.endDate,
      ),
    );
    if (lane === -1) lane = occupied.length;
    (occupied[lane] ??= []).push(range);
    lanes.set(range.id, lane);
  }
  return lanes;
}

/**
 * Lanes for the week view's all-day area. Legs are packed first so they keep
 * the top rows; all-day notes then take the first lane free on their dates,
 * sitting beside a leg rather than below every leg. Legs and notes are keyed
 * separately because their ids come from different tables.
 */
export function packAllDayLanes(
  legs: LaneRange[],
  notes: LaneRange[],
): { legLanes: Map<string, number>; noteLanes: Map<string, number> } {
  const occupied: LaneRange[][] = [];
  const legLanes = packDateRangeLanes(sortByStart(legs), new Map(), occupied);
  const noteLanes = packDateRangeLanes(sortByStart(notes), new Map(), occupied);
  return { legLanes, noteLanes };
}
